import { Building2, Mail, User } from "lucide-react";
import type { FC } from "react";

import { FormRow, FormSection } from "@/components/form";
import { Skeleton } from "@/components/ui/skeleton";

const FieldSkeleton: FC<{ label: string }> = ({ label }) => (
  <div className="flex min-w-0 flex-1 basis-0 flex-col gap-2">
    <span className="text-sm font-medium text-fg-3">{label}</span>
    <Skeleton className="h-8 w-full" />
  </div>
);

/** Section-shaped placeholder shown until the individual has loaded. */
export const SettingsLoadingPreview: FC<{
  variant: "personal" | "employer";
}> = ({ variant }) => {
  const isPersonal = variant === "personal";
  return (
    <div
      className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-background"
      aria-busy="true"
    >
      <FormSection
        variant="band"
        icon={isPersonal ? User : Building2}
        title={isPersonal ? "Biographical Information" : "Employer Information"}
        className="[&>div:first-child]:border-t-0"
      >
        {isPersonal ? (
          <>
            <FormRow collapseBelow="lg">
              <FieldSkeleton label="First Name" />
              <FieldSkeleton label="Middle Name" />
              <FieldSkeleton label="Last Name" />
            </FormRow>
            <FormRow collapseBelow="lg">
              <FieldSkeleton label="Title" />
              <FieldSkeleton label="Pronouns" />
              <FieldSkeleton label="Date of Birth" />
            </FormRow>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-fg-3">
                Is the employer the petitioner?
              </span>
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <FormRow>
              <FieldSkeleton label="Legal Business Name" />
              <FieldSkeleton label="Has DBA" />
            </FormRow>
          </>
        )}
      </FormSection>
      <FormSection
        variant="band"
        icon={Mail}
        title={
          isPersonal ? "Contact Information" : "Employer Contact Information"
        }
      >
        <FormRow>
          <FieldSkeleton
            label={isPersonal ? "Email Address" : "Contact Name"}
          />
          <FieldSkeleton
            label={isPersonal ? "Phone Number" : "Contact Title"}
          />
        </FormRow>
        <FormRow>
          <FieldSkeleton
            label={isPersonal ? "Street Address" : "Business Address"}
          />
          <FieldSkeleton label="Country" />
        </FormRow>
      </FormSection>
      <span className="sr-only">Loading case settings...</span>
    </div>
  );
};
